import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { useBranding } from "../../context/BrandingContext";
import { Mail, Lock, Eye, EyeOff, Loader2, Shield } from "lucide-react";
import { auth, db } from "../../firebase";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

// ── Shared page shell ─────────────────────────────────────────────────────────
// Dark full-page background with radial brand glow + language switcher
function PageShell({ accent, lang, setLang, LANG_OPTIONS, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden"
      style={{ backgroundColor: "#0F172A" }}>

      {/* Radial glow at page center */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 70% 55% at 50% 42%, ${accent}22 0%, transparent 100%)`,
        }} />

      {/* Language switcher — top right */}
      <div className="absolute top-5 right-5 flex gap-1 z-20">
        {LANG_OPTIONS.map(l => (
          <button
            key={l.value}
            onClick={() => setLang(l.value)}
            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition ${
              lang === l.value
                ? "bg-[#F59E0B] text-white"
                : "text-white/35 hover:text-white/60"
            }`}>
            {l.label}
          </button>
        ))}
      </div>

      {/* Card */}
      <div className="relative z-10 w-full max-w-[400px]">
        {children}
      </div>
    </div>
  );
}

// ── Shared card ───────────────────────────────────────────────────────────────
function Card({ children }) {
  return (
    <div className="bg-white rounded-3xl w-full"
      style={{ boxShadow: "0 32px 72px -12px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)" }}>
      {children}
    </div>
  );
}

// ── Brand mark (logo + name + accent line) ────────────────────────────────────
function BrandMark({ branding, accent }) {
  const name = branding.platform_name || "Pinpoint";
  return (
    <div className="flex flex-col items-center pt-10 pb-8 px-10 border-b border-gray-100">
      {branding.logo_url ? (
        <img
          src={branding.logo_url}
          alt={name}
          className="w-14 h-14 object-contain mb-4"
          style={{ filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.18))" }}
        />
      ) : (
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
          style={{
            backgroundColor: accent,
            boxShadow: `0 8px 24px ${accent}55`,
          }}>
          <span className="text-white text-2xl font-bold select-none">
            {name.charAt(0).toUpperCase()}
          </span>
        </div>
      )}
      <span className="text-gray-900 font-extrabold tracking-[0.18em] text-base select-none">
        {name.toUpperCase()}
      </span>
      <span className="w-6 h-0.5 rounded-full mt-3" style={{ backgroundColor: accent }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function Login() {
  const [email,         setEmail]         = useState("");
  const [password,      setPassword]      = useState("");
  const [showPassword,  setShowPassword]  = useState(false);
  const [error,         setError]         = useState("");
  const [loading,       setLoading]       = useState(false);
  const [suspended,     setSuspended]     = useState(false);

  const [loginStep,    setLoginStep]    = useState("credentials");
  const [twoFACode,    setTwoFACode]    = useState("");
  const [twoFAError,   setTwoFAError]   = useState("");
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [tempUser,     setTempUser]     = useState(null);

  const { login }   = useAuth();
  const navigate    = useNavigate();
  const { lang, setLang, LANG_OPTIONS, t } = useLanguage();
  const { branding } = useBranding();

  const accent = branding.primary_color || "#F59E0B";

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    setSuspended(false);
    setLoading(true);
    try {
      const { role } = await login(email, password);
      if (role === "super_admin") {
        const snap = await getDoc(doc(db, "platform", "super_admin_2fa"));
        const is2FAEnabled = snap.exists() && snap.data().enabled === true;
        if (is2FAEnabled) {
          await signOut(auth);
          setTempUser({ email, password });
          setLoginStep("twofa");
          setLoading(false);
          return;
        }
        navigate("/superadmin");
      }
      else if (role === "admin")      navigate("/admin");
      else if (role === "supervisor") navigate("/supervisor");
      else if (role === "operator")   navigate("/operator");
      else if (role === "vendor")     navigate("/vendor");
      else setError("Your account does not have dashboard access.");
    } catch (err) {
      if (err.code === "account_suspended" || err.message === "ACCOUNT_SUSPENDED") {
        setSuspended(true);
      } else if (err.code === "account_disabled" || err.message === "ACCOUNT_DISABLED") {
        setError(t("login_error_disabled") || "Your account has been disabled. Contact your administrator.");
      } else {
        setError(t("login_error_generic") || "Incorrect email or password. Please try again.");
      }
    }
    setLoading(false);
  }

  async function handleVerify2FA(e) {
    e.preventDefault();
    if (twoFACode.length !== 6) { setTwoFAError(t("err_2fa_code_length")); return; }
    setTwoFAError("");
    setVerifying2FA(true);
    try {
      await signInWithEmailAndPassword(auth, tempUser.email, tempUser.password);
      const fns    = getFunctions(undefined, "europe-west1");
      const check  = httpsCallable(fns, "check2FACode");
      const result = await check({ code: twoFACode });
      if (result.data.valid === true) {
        navigate("/superadmin");
      } else {
        await signOut(auth);
        setTwoFAError(result.data.error || t("err_2fa_invalid"));
      }
    } catch (_err) {
      try { await signOut(auth); } catch (_) {}
      setTwoFAError(t("err_2fa_invalid"));
    }
    setVerifying2FA(false);
  }

  const shellProps = { accent, lang, setLang, LANG_OPTIONS };

  // ── Suspended ─────────────────────────────────────────────────────────────
  if (suspended) {
    return (
      <PageShell {...shellProps}>
        <Card>
          <BrandMark branding={branding} accent={accent} />
          <div className="px-10 py-8 flex flex-col items-center text-center">
            <div className="w-11 h-11 bg-red-50 rounded-2xl flex items-center justify-center mb-4">
              <Lock className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-sm font-semibold text-gray-800 mb-5">
              {t("lbl_account_suspended") || "Account Suspended"}
            </p>
            <div className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 mb-6">
              <p className="text-xs text-gray-400 mb-1">
                {t("login_suspended_contact") || "Contact support:"}
              </p>
              <p className="text-sm font-semibold text-gray-800">support@pinpoint.ae</p>
            </div>
            <button
              onClick={() => setSuspended(false)}
              className="text-sm text-gray-400 hover:text-gray-600 transition flex items-center gap-1.5">
              ← {t("login_suspended_back") || "Back to login"}
            </button>
          </div>
        </Card>
      </PageShell>
    );
  }

  // ── 2FA ───────────────────────────────────────────────────────────────────
  if (loginStep === "twofa") {
    return (
      <PageShell {...shellProps}>
        <Card>
          <BrandMark branding={branding} accent={accent} />
          <div className="px-10 py-8">
            <div className="flex flex-col items-center mb-7">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4"
                style={{ backgroundColor: accent + "20" }}>
                <Shield className="w-5 h-5" style={{ color: accent }} />
              </div>
              <p className="text-sm font-semibold text-gray-800 text-center">
                {t("lbl_2fa_verification") || "Two-Factor Verification"}
              </p>
              <p className="text-xs text-gray-400 text-center mt-1.5 leading-relaxed">
                {t("lbl_2fa_open_app") || "Open your authenticator app and enter the 6-digit code."}
              </p>
            </div>

            {twoFAError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-3 mb-4 text-xs text-center">
                {twoFAError}
              </div>
            )}

            <form onSubmit={handleVerify2FA} className="space-y-3">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={twoFACode}
                onChange={e => setTwoFACode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                className="w-full border border-gray-200 rounded-2xl px-4 py-3.5 text-center text-2xl font-mono tracking-[0.5em] text-gray-900 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent transition"
              />
              <button
                type="submit"
                disabled={verifying2FA || twoFACode.length !== 6}
                className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold rounded-2xl py-3.5 text-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {verifying2FA
                  ? <><Loader2 className="w-4 h-4 animate-spin" />{t("lbl_verifying") || "Verifying..."}</>
                  : t("btn_verify") || "Verify"
                }
              </button>
            </form>

            <button
              onClick={() => { setLoginStep("credentials"); setTwoFACode(""); setTwoFAError(""); setTempUser(null); }}
              className="mt-5 w-full text-xs text-gray-400 hover:text-gray-600 transition text-center">
              ← {t("btn_back_to_login") || "Back to Login"}
            </button>
          </div>
        </Card>
      </PageShell>
    );
  }

  // ── Main login ────────────────────────────────────────────────────────────
  return (
    <PageShell {...shellProps}>
      <Card>
        <BrandMark branding={branding} accent={accent} />

        <div className="px-10 py-8">

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-3 mb-5 text-xs leading-relaxed">
              {error}
            </div>
          )}

          {/* Form — no labels, icon + placeholder */}
          <form onSubmit={handleLogin} className="space-y-3">

            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder={t("login_email") || "Email Address"}
                className="w-full border border-gray-200 rounded-2xl pl-10 pr-4 py-3.5 text-sm text-gray-900 placeholder-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent transition"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder={t("login_password") || "Password"}
                className="w-full border border-gray-200 rounded-2xl pl-10 pr-12 py-3.5 text-sm text-gray-900 placeholder-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent transition"
              />
              <button
                type="button"
                onClick={() => setShowPassword(p => !p)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold rounded-2xl py-3.5 text-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("login_signing_in") || "Signing in..."}
                </>
              ) : (
                t("login_submit") || "Sign In"
              )}
            </button>

          </form>
        </div>
      </Card>
    </PageShell>
  );
}

export default Login;
